import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useHistory, useParams } from "react-router-dom";
import { Box, Button, Chip, CircularProgress, Tooltip, Typography } from "@mui/material";
import { ArrowBack, ErrorOutline } from "@mui/icons-material";
import { DataGrid, GridCellParams, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { ptBR } from "@mui/x-data-grid/locales";

import api from "services/api";
import { useAlert } from "contexts/alert";
import { cpfMask } from "utils/cpf";

import { IBateCadastralApi } from "./types";
import {
  CampoEditavel,
  LABEL_CAMPO,
  LinhaRetificacao,
  RetificacaoPendente,
  RetificarResposta,
  campoDaInconsistencia,
} from "./Retificar.types";

/** A ordem das colunas editáveis na grade. */
const CAMPOS: CampoEditavel[] = [
  "nome",
  "plano",
  "sexo",
  "dataNascimento",
  "dataAdmissao",
  "grauParentesco",
  "cpfTitular",
  "vigenciaInicial",
  "vigenciaFinal",
  "valorPlano",
];

/**
 * Pivota os achados do bate de uma linha por achado para **uma linha por pessoa**.
 *
 * O `GET` do bate devolve um registro por inconsistência, então quem tem três problemas aparece
 * três vezes. Aqui cada pessoa vira uma linha, e o conjunto de campos divergentes dela sai da
 * coluna de texto do achado.
 */
function pivotarPorPessoa(people: IBateCadastralApi["people"]): LinhaRetificacao[] {
  const porCpf = new Map<string, LinhaRetificacao>();

  people.forEach((p) => {
    const linha =
      porCpf.get(p.taxPayerId) ??
      ({
        id: p.taxPayerId,
        cpf: p.taxPayerId,
        nome: p.name ?? "",
        tipo: p.beneficiaryType,
        camposDivergentes: [],
        achadosDePresenca: [],
        valores: {},
        valoresOperadora: {},
        valoresOriginais: {},
      } as LinhaRetificacao);

    const campo = campoDaInconsistencia(p.inconsistency);

    if (campo) {
      if (!linha.camposDivergentes.includes(campo)) linha.camposDivergentes.push(campo);
      /**
       * `valueSystem` é o nosso valor e é o que se edita — a posição da operadora é o fato
       * executado, então a divergência é erro do nosso lado.
       */
      linha.valores[campo] = p.valueSystem;
      linha.valoresOriginais[campo] = p.valueSystem;
      linha.valoresOperadora[campo] = p.valuePosicaoCadastral;
    } else {
      linha.achadosDePresenca.push(p.inconsistency);
    }

    porCpf.set(p.taxPayerId, linha);
  });

  return [...porCpf.values()];
}

export function RetificarView() {
  const { workspaceId, templateId, workspaceActivityId } = useParams() as {
    workspaceId: string;
    templateId: string;
    workspaceActivityId: string;
  };
  const history = useHistory();
  const { openAlert } = useAlert();
  const queryClient = useQueryClient();

  /**
   * As edições vivem aqui até existir backend. `processRowUpdate` devolve a linha e o React Query
   * não é a fonte da verdade dela, então o estado local é o que mantém o valor digitado na tela.
   */
  const [editado, setEditado] = useState<Record<string, Partial<Record<CampoEditavel, string | null>>>>({});
  const [pendentes, setPendentes] = useState<RetificacaoPendente[]>([]);
  const [enviando, setEnviando] = useState(false);

  const { data, isFetching, isFetched, isError } = useQuery({
    queryKey: ["bate-cadastral", workspaceId],
    queryFn: () => api.get<{ data: IBateCadastralApi }>(`/v2/bate-cadastral/${workspaceActivityId}`),
    select: (response) => response.data.data,
  });

  const linhas = useMemo(() => {
    const base = pivotarPorPessoa(data?.people ?? []);

    /** Sobrepõe o que já foi digitado, para a célula não voltar ao valor original a cada render. */
    return base.map((linha) => ({
      ...linha,
      valores: { ...linha.valores, ...(editado[linha.cpf] ?? {}) },
    }));
  }, [data, editado]);

  const colunas: GridColDef[] = [
    {
      field: "cpf",
      headerName: "CPF",
      width: 150,
      editable: false,
      renderCell: (params: GridRenderCellParams) => cpfMask(String(params.value)),
    },
    { field: "nomeBeneficiario", headerName: "Beneficiário", width: 240, editable: false },
    {
      field: "tipo",
      headerName: "Tipo",
      width: 110,
      editable: false,
      renderCell: (params: GridRenderCellParams) =>
        params.value === "employee" ? "Titular" : "Dependente",
    },
    {
      field: "pendencias",
      headerName: "Divergências",
      width: 130,
      editable: false,
      renderCell: (params: GridRenderCellParams) => {
        const linha = params.row as LinhaRetificacao & { pendencias: number };
        const total = linha.camposDivergentes.length + linha.achadosDePresenca.length;

        return <Chip size="small" label={total} color={total > 1 ? "warning" : "default"} />;
      },
    },
    ...CAMPOS.map<GridColDef>((campo) => ({
      field: campo,
      headerName: LABEL_CAMPO[campo],
      width: campo === "nome" ? 240 : 170,
      /**
       * A coluna é declarada editável, mas o `isCellEditable` da grade decide **por célula**: só
       * abre para edição onde aquela pessoa tem divergência naquele campo.
       */
      editable: true,
      renderCell: (params: GridRenderCellParams) => {
        const linha = params.row as LinhaRetificacao;
        const divergente = linha.camposDivergentes.includes(campo);

        if (!divergente) return <span style={{ color: "#C4C4C4" }}>—</span>;

        const daOperadora = linha.valoresOperadora[campo];
        /**
         * Sai do vermelho quando o valor muda do original, não quando iguala o da operadora:
         * o que a cor comunica é "ainda não mexi nisto", não "está certo".
         */
        const editado = params.value !== linha.valoresOriginais[campo];

        return (
          <Tooltip
            arrow
            placement="top"
            title={editado ? "Editado, ainda não enviado" : `Na operadora: ${daOperadora ?? "(vazio)"}`}
          >
            <span
              style={{
                color: editado ? "#191C1A" : "#B3261E",
                fontWeight: editado ? 400 : 600,
                textDecoration: editado ? "none" : "underline dotted",
                textUnderlineOffset: "3px",
                cursor: "pointer",
              }}
            >
              {params.value ?? "(vazio)"}
            </span>
          </Tooltip>
        );
      },
    })),
  ];

  const rows = linhas.map((linha) => ({
    ...linha,
    nomeBeneficiario: linha.nome,
    pendencias: linha.camposDivergentes.length + linha.achadosDePresenca.length,
    ...CAMPOS.reduce<Record<string, string | null>>((acc, campo) => {
      acc[campo] = linha.valores[campo] ?? null;
      return acc;
    }, {}),
  }));

  /** Só a célula de um campo divergente daquela pessoa abre para edição. */
  function isCellEditable(params: GridCellParams) {
    const campo = params.field as CampoEditavel;

    if (!CAMPOS.includes(campo)) return false;

    return (params.row as LinhaRetificacao).camposDivergentes.includes(campo);
  }

  /**
   * A edição fica no cliente até o envio: `processRowUpdate` só acumula a pendência, e o `PATCH`
   * sai de uma vez no botão. Isso evita uma requisição por tecla e deixa o usuário revisar antes.
   */
  async function processRowUpdate(novo: any, antigo: any) {
    const campo = CAMPOS.find((c) => novo[c] !== antigo[c]);

    if (!campo) return antigo;

    const retificacao: RetificacaoPendente = {
      cpf: novo.cpf,
      campo,
      valorAnterior: antigo[campo] ?? null,
      valorNovo: novo[campo] ?? null,
    };

    setEditado((atual) => ({
      ...atual,
      [novo.cpf]: { ...(atual[novo.cpf] ?? {}), [campo]: retificacao.valorNovo },
    }));
    setPendentes((atual) => [...atual.filter((p) => !(p.cpf === retificacao.cpf && p.campo === campo)), retificacao]);

    return novo;
  }

  /** Envia tudo de uma vez e recarrega o bate, para as células refletirem o que foi gravado. */
  async function enviar() {
    try {
      setEnviando(true);

      const { data: resposta } = await api.patch<{ data: RetificarResposta }>(
        `/v2/bate-cadastral/${workspaceActivityId}/retificar`,
        { retificacoes: pendentes.map(({ cpf, campo, valorNovo }) => ({ cpf, campo, valorNovo })) }
      );

      const { pessoas, campos, ignoradas, persistidas } = resposta.data;

      openAlert(
        `${campos} campos corrigidos em ${pessoas} beneficiários. ` +
          `Persistido: ${persistidas.atualizadas} atualizados, ${persistidas.criadas} criados.` +
          (ignoradas.length ? ` ${ignoradas.length} ignorados.` : ""),
        ignoradas.length ? "warning" : "success"
      );

      setPendentes([]);
      setEditado({});
      await queryClient.invalidateQueries({ queryKey: ["bate-cadastral", workspaceId] });
    } catch (erro) {
      // @ts-ignore
      openAlert(erro?.response?.data?.data?.message ?? "Erro ao enviar as retificações.", "error");
    } finally {
      setEnviando(false);
    }
  }

  function renderBody() {
    if (!isFetched && isFetching) {
      return (
        <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
          <CircularProgress size={30} thickness={4} />
        </Box>
      );
    }

    if (isError) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px" gap={3} textAlign="center">
          <ErrorOutline sx={{ fontSize: 40, color: "error.main" }} />
          <Typography variant="h6">Ops! Algo deu errado</Typography>
          <Typography variant="body1" color="textSecondary" maxWidth={420}>
            Não foi possível carregar as inconsistências. Tente novamente.
          </Typography>
        </Box>
      );
    }

    return (
      <div style={{ height: 620, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={colunas}
          isCellEditable={isCellEditable}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={(erro) => openAlert(String(erro), "error")}
          initialState={{ pagination: { paginationModel: { page: 0, pageSize: 25 } } }}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          getRowHeight={() => 56}
          localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
          style={{ backgroundColor: "white" }}
          sx={{
            "& .MuiDataGrid-cell": { fontSize: "0.875rem" },
            "& .MuiDataGrid-columnHeader": { fontSize: "0.875rem", fontWeight: 600 },
          }}
        />
      </div>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ marginBottom: "8px", display: "flex", alignItems: "center" }}>
        {/* Voltar proprio: a Page volta para a lista de atividades, nao para o bate. */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => history.push(`/v2/workspaces/${workspaceId}/templates/${templateId}/bate-cadastral/${workspaceActivityId}`)}
          sx={{ textTransform: "none" }}
        />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Exceções</Typography>
      </Box>

      <Typography sx={{ color: "text.secondary", fontSize: "14px", marginBottom: "16px" }}>
        Uma linha por beneficiário. <strong>Em vermelho, o valor da nossa base que precisa ser
        corrigido</strong> — passe o mouse para ver o que a operadora informa, que é o valor
        correto. Ao editar, a célula volta ao normal. As colunas com <strong>—</strong> não têm
        divergência nesta pessoa.
      </Typography>

      <Box sx={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
        <Chip label={`${rows.length} beneficiários`} />
        <Chip label={`${pendentes.length} edições pendentes`} color={pendentes.length ? "warning" : "default"} />
      </Box>

      {renderBody()}

      <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: "16px", gap: "8px" }}>
        <Button
          variant="contained"
          disabled={!pendentes.length || enviando}
          onClick={enviar}
          sx={{ textTransform: "none", minWidth: 180 }}
        >
          {enviando ? <CircularProgress size="20px" /> : "Enviar retificações"}
        </Button>
      </Box>
    </Box>
  );
}
